import React, { useState } from "react";
import {
  LazyAnimatePresence,
  MotionDiv,
} from "@/components/LazyFramerMotion";
import { usePrefersReducedMotion } from "@/lib/usePrefersReducedMotion";

import { HomeRevealStripItem, HomeSectionTitle } from "@/components/HomeMotionPrimitivesStatic";

const FAQ_ITEMS = [
  {
    question: "Are all products on AFOMA handmade?",
    answer:
      "Yes, every item is crafted by skilled artisans. We focus on authentic, handmade, and ethically sourced goods from around the world.",
  },
  {
    question: "Where do your products come from?",
    answer:
      "Our products come from global artisans, including creators in Africa, South Asia, Latin America, and other emerging markets",
  },
  {
    question: "Do you offer returns or refunds?",
    answer:
      "Refunds may be issued if an item arrives damaged or defective. Returns are handled according to each seller’s individual return policy, which is listed on their shop page. If you’re unsure, contact the seller directly before purchasing.",
  },
  {
    question: "Why should I shop on AFOMA?",
    answer:
      "Your purchase supports small businesses and artisans globally. It’s ethical shopping that empowers communities and celebrates culture.",
  },
  {
    question:
      "What should I consider about shipping costs for international purchases?",
    answer:
      "Shipping costs for international purchases may vary based on your location and the seller's location. Please be aware that your country's regulations may incur customs duties on the delivery of your product. Despite the potential of incurring higher charges for international shipping, rest assured that your chosen artisanal creations will be carefully packaged and delivered with care.",
  },
];

const easeOut = [0.22, 1, 0.36, 1];

function ChevronRightIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const Faqs = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const reduce = usePrefersReducedMotion();

  const toggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="bg-white">
      <div className="mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16 xl:max-w-5xl xl:py-20">
        <div className="mb-8 text-center md:mb-10">
          <HomeSectionTitle className="text-2xl font-bold tracking-tight text-neutral-900 md:text-3xl lg:text-4xl">
            Got Questions? We&apos;ve Got Answers!
          </HomeSectionTitle>
        </div>
        <div className="flex flex-col gap-4 md:gap-5">
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <HomeRevealStripItem key={item.question} index={index}>
                <div
                  className={`overflow-hidden rounded-2xl transition-colors duration-200 md:rounded-3xl ${
                    isOpen ? "bg-primary" : "bg-orange-100"
                  }`}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    className="flex w-full cursor-pointer items-start justify-between gap-4 p-5 text-left md:items-center md:p-6"
                    onClick={() => toggle(index)}
                  >
                    <span
                      className={`min-w-0 flex-1 text-base font-bold leading-snug md:text-lg ${
                        isOpen ? "text-white" : "text-neutral-900"
                      }`}
                    >
                      {item.question}
                    </span>
                    <span
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-sm"
                      aria-hidden
                    >
                      {isOpen ? (
                        <CloseIcon className="text-neutral-800" />
                      ) : (
                        <ChevronRightIcon className="text-neutral-800" />
                      )}
                    </span>
                  </button>
                  <LazyAnimatePresence initial={false}>
                    {isOpen ? (
                      <MotionDiv
                        key="faq-body"
                        initial={reduce ? false : { height: 0, opacity: 0 }}
                        animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
                        exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        transition={
                          reduce
                            ? { duration: 0.15 }
                            : { duration: 0.32, ease: easeOut }
                        }
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 pt-0 md:px-6 md:pb-6">
                          <p className="text-sm font-normal leading-relaxed text-white/90 md:text-base">
                            {item.answer}
                          </p>
                        </div>
                      </MotionDiv>
                    ) : null}
                  </LazyAnimatePresence>
                </div>
              </HomeRevealStripItem>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Faqs;
